export type RefreshRequest = {
  /** Include the dataset and every dimension that changes its data (season/week/model). */
  key: string;
  serverTimestamp: number;
  hasData: boolean;
  stale: boolean;
  force?: boolean;
  retry?: boolean;
  load: (force: boolean, signal: AbortSignal) => Promise<void>;
  onError: (error: unknown) => void;
};

type RefreshState = {
  completed?: number;
  failed?: number;
  active?: { timestamp: number; controller: AbortController };
  pending?: RefreshRequest;
};

/** Runtime only: local write times never acknowledge an announcement received during a load. */
export const createSyncRefreshCoordinator = () => {
  const scopes = new Map<string, RefreshState>();
  let owners = 0;

  const request = (next: RefreshRequest): void => {
    const state = scopes.get(next.key) ?? {};
    scopes.set(next.key, state);
    if (state.active) {
      if (next.force || next.retry || next.serverTimestamp > state.active.timestamp) {
        // Coalesce explicit reloads with the newest queued announcement. Neither a
        // later ordinary render nor an older forced request may discard that intent.
        const pending = state.pending;
        const latest = pending && pending.serverTimestamp > next.serverTimestamp ? pending : next;
        state.pending = {
          ...latest,
          force: !!pending?.force || !!next.force,
          retry: !!pending?.retry || !!next.retry,
        };
      }
      return;
    }
    if (!next.retry && !next.force && state.failed !== undefined && next.serverTimestamp <= state.failed) return;
    const newer = state.completed !== undefined && next.serverTimestamp > state.completed;
    const handled = state.completed !== undefined && next.serverTimestamp <= state.completed;
    if (!next.retry && !next.force && handled) return;
    if (!next.retry && !next.force && next.hasData && !next.stale && !newer) {
      // Remember the observed baseline even when the existing cache needs no load.
      state.completed = next.serverTimestamp;
      return;
    }

    const active = { timestamp: next.serverTimestamp, controller: new AbortController() };
    state.active = active;
    // A failed write or interrupted batch may have advanced some local stores. Retry the source.
    const force = !!next.force || next.stale || newer || state.failed !== undefined;
    void (async () => {
      try {
        await next.load(force, active.controller.signal);
        if (active.controller.signal.aborted) return;
        state.completed = active.timestamp;
        state.failed = undefined;
      } catch (error) {
        if (active.controller.signal.aborted) return;
        state.failed = active.timestamp;
        next.onError(error);
      } finally {
        if (state.active === active) state.active = undefined;
        const pending = state.pending;
        state.pending = undefined;
        if (pending && !active.controller.signal.aborted) {
          // Pending B must fetch even if A's later local timestamp now looks current.
          request({ ...pending, stale: true });
        }
      }
    })();
  };

  return {
    request,
    /** Shared consumers keep one coordinator alive; the last unmount cancels outstanding work. */
    retain: () => {
      owners++;
      return () => {
        if (--owners !== 0) return;
        scopes.forEach((state) => state.active?.controller.abort());
        scopes.clear();
      };
    },
  };
};
